import React, { useEffect, useState } from 'react';
import AuditLogTable from './AuditLogTable';
import FiltersPane from './FiltersPane';
import colors from '../../../constants/ColourConstants';
import CustomButton from '../../common/Button/Button';
import { Button, Pagination, Space, Tooltip, message } from 'antd';
import { ExportOutlined, FilterOutlined } from '@ant-design/icons';
import { getAuditExport, getAuditLog } from '../../../services/AuditLogAPI';
import moment from 'moment';

export const initFilters = {
  type: '',
  fieldChanged: [],
  modifiedBy: '',
  fixedRangeForCreated: 'Last 30 days',
  idList: [],
};

const AuditLogs = () => {
  const [auditLogs, setAuditLogs] = useState({});

  const [openFilters, setOpenFilters] = useState(false);
  const [filterValues, setFilterValues] = useState(initFilters);
  const [exportLoader, setExportLoader] = useState(false);

  const [tableId, setTableId] = useState(1);
  const [currentData, setCurrentData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);

  const [pageLoader, setPageLoader] = useState(true);
  const [tableLoader, setTableLoader] = useState(false);
  const [error, setError] = useState(false);

  const [selected, setSelected] = useState('transactions');
  const [sortField, setSortField] = useState({});

  const TableHeader = () => (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        backgroundColor: colors.colorSplit,
        margin: '-16px',
        padding: '8px',
      }}
    >
      <div
        style={{
          display: 'flex',
          gap: '16px',
          alignItems: 'center',
        }}
      >
        <Tooltip title={'Export to CSV'}>
          <CustomButton
            size="large"
            disabled={totalRecords <= 0}
            loading={exportLoader}
            variant="outlined"
            icon={<ExportOutlined />}
            shape="circle"
            onClick={() => handleExportCSV()}
          />
        </Tooltip>
      </div>
      <div
        style={{
          display: 'flex',
          gap: '16px',
          justifyContent: 'flex-end',
          alignItems: 'center',
        }}
      >
        <CustomButton
          shape="round"
          type="default"
          variant="outlined"
          icon={<FilterOutlined />}
          size="large"
          onClick={() => setOpenFilters(true)}
        >
          Filter
        </CustomButton>
      </div>
    </div>
  );

  useEffect(() => {
    !pageLoader && handleApplyFilters();
  }, [pageSize]);

  useEffect(() => {
    if (auditLogs[currentPage]) {
      setCurrentData(auditLogs[currentPage]);
    } else {
      handleAuditLogs();
    }
  }, [currentPage]);

  useEffect(() => {
    handleAuditLogs();
  }, [sortField]);

  useEffect(() => {
    setCurrentData(auditLogs[currentPage]);
  }, [auditLogs]);

  const handlePageChange = (page) => setCurrentPage(page);
  const handlePageSizeChange = (_current, size) => setPageSize(size);

  const handleSelect = (value) => {
    setTableId(() => tableId + 1);
    setSelected(value);
    const values = {
      ...filterValues,
      fieldChanged: [],
      type: value,
      idList: [],
    };
    setFilterValues(values);
    setSortField({});
    setAuditLogs([]);
    if (currentPage !== 1) {
      setCurrentPage(1);
    } else {
      handleAuditLogs(values, 'selectTabs');
    }
  };

  const getPayload = () => {
    const {
      fieldChanged,
      modifiedBy,
      fixedRangeForCreated,
      'fixedRangeForCreated.customDates': customDate,
      sortOrder,
      idList,
    } = filterValues;
    return {
      type: selected,
      fieldChanged: fieldChanged?.filter((i) => i).toString(),
      idList: idList?.filter((i) => i).toString(),
      modifiedBy,
      fixedRangeForCreated: fixedRangeForCreated,
      startDate: customDate?.[0],
      endDate: customDate?.[1],
      page: currentPage,
      [sortField.field]: sortField?.order,
      size: pageSize,
      order: sortOrder,
    };
  };

  const handleAuditLogs = (rest, from) => {
    setTableLoader(true);
    let payload = getPayload();
    if (from === 'selectTabs') {
      payload = { ...payload, type: rest?.type, fieldChanged: [] };
    }

    getAuditLog(payload)
      .then((data) => {
        if (!data) return;
        console.log('11111', data);
        setAuditLogs({
          ...auditLogs,
          [`${data.pagination.currentPage}`]: transformData(data),
        });
        setTotalRecords(data?.pagination?.totalRows || 0);
        setTableLoader(false);
        setPageLoader(false);
      })
      .catch((err) => {
        if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') {
          console.log('Request cancelled');
        } else {
          setError(true);
          message.error('Unable to fetch audit logs');
          setTableLoader(false);
          setPageLoader(false);
        }
      });
  };

  const transformData = (data) => {
    const { rows, columns } = data;
    let map = rows.map((row) => {
      return columns.reduce((obj, col, index) => {
        obj[col] = row[index];
        obj['key'] = obj['recordId'];
        return obj;
      }, {});
    });
    return map;
  };

  const handleExportCSV = async () => {
    setExportLoader(true);
    let payload = getPayload();
    const type = payload?.type;
    try {
      const data = await getAuditExport(payload);

      const blob = new Blob([data]);
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      const timestamp = moment().format('YYYY-MM-DD_HH-mm-ss');
      link.setAttribute('download', `${type}_audit_log_${timestamp}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      message.success(`Audit log ${type} CSV file is downloaded.`);
    } catch {
      message.error(`Unable to download ${type} audit log CSV file`);
    } finally {
      setExportLoader(false);
    }
  };

  const handleApplyFilters = () => {
    setOpenFilters(false);
    setTableId(() => tableId + 1);
    setAuditLogs(() => {
      return {};
    });

    if (currentPage !== 1) {
      setCurrentPage(() => {
        return 1;
      });
    } else {
      handleAuditLogs();
    }
  };

  function handleApplysort(rest) {
    setTableId(() => tableId + 1);
    setAuditLogs(() => {
      return {};
    });
    setCurrentPage(() => {
      return 1;
    });
    setSortField(() => {
      return rest;
    });
  }

  return (
    <>
      <div
        style={{ display: 'flex', alignItems: 'center', position: 'relative' }}
      >
        <div
          style={{
            position: 'absolute',
            width: '100%',
            border: '1px solid #D9D9D9',
          }}
        />
        <Space.Compact
          block
          style={{
            justifyContent: 'center',
          }}
        >
          <Button
            type={selected === 'transactions' && 'primary'}
            onClick={() => handleSelect('transactions')}
          >
            Transactions
          </Button>
          <Button
            type={selected === 'invoices' && 'primary'}
            onClick={() => handleSelect('invoices')}
          >
            Invoices
          </Button>
          <Button
            type={selected === 'payouts' && 'primary'}
            onClick={() => handleSelect('payouts')}
          >
            Payouts
          </Button>
        </Space.Compact>
      </div>
      {error ? null : (
        <>
          <AuditLogTable
            key={`table-${tableId}`}
            title={TableHeader}
            sortField={sortField}
            loading={tableLoader}
            data={currentData}
            handleSort={handleApplysort}
          />
          <Pagination
            style={{
              padding: '16px 0',
              display: 'flex',
              justifyContent: 'end',
            }}
            pageSize={pageSize}
            current={currentPage}
            total={totalRecords}
            showSizeChanger={true}
            onChange={handlePageChange}
            onShowSizeChange={handlePageSizeChange}
          />
          <FiltersPane
            openFilters={openFilters}
            type={selected}
            filterValues={filterValues}
            setFilterValues={setFilterValues}
            setOpenFilters={setOpenFilters}
            handleApply={handleApplyFilters}
          />
        </>
      )}
    </>
  );
};

export default AuditLogs;
